'use client';

import AdvisorIllustration from '@/assets/aura/app/advisor/common/skills.svg';
import AnalystIllustration from '@/assets/aura/app/analyst/common/skills.svg';
import AnimatorIllustration from '@/assets/aura/app/animator/common/skills.svg';
import CoachIllustration from '@/assets/aura/app/coach/common/skills.svg';
import PeacemakerIllustration from '@/assets/aura/app/peacemaker/common/skills.svg';
import PilotIllustration from '@/assets/aura/app/pilot/common/skills.svg';
import PlannerIllustration from '@/assets/aura/app/planner/common/skills.svg';
import DefaultIllustration from '@/assets/aura/app/primary/common/skills.svg';
import ProtectorIllustration from '@/assets/aura/app/protector/common/skills.svg';
import QuarkLogo from '@/assets/logos/quark/square-dark.svg';
import { Trans, t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Box, Stack, Typography } from '@mui/material';
import { Button } from '@prismo-io/design-system';
import { useMe } from '@prismo-io/feature-auth/providers/me';
import { Application, ButtonLink, translate } from '@prismo-io/router';
import {
  AuraProfileCode,
  type AuraProfileCodeT,
  LanguageCode,
  type LanguageCodeT,
} from '@prismo-io/schemas';
import Image from 'next/image';
import Link from 'next/link';
import { type FC, useMemo } from 'react';

type Props = {
  order: number;
  userUrl: string;
  profileType?: AuraProfileCodeT;
  firstSkill?: string | undefined;
};

const buildQuarkUrl = (locale: LanguageCodeT) => {
  // eslint-disable-next-line no-constant-condition, prettier/prettier
  return translate({
    app: Application.QUARK,
    path: '/',
    addHostname: true,
    locale,
  });
};

export const QuarkCard: FC<Props> = (props: Props) => {
  const { profileType, firstSkill = null, userUrl, order } = props;

  const me = useMe();

  const isMe = me && me.url === userUrl;

  const img = useMemo(() => {
    switch (profileType) {
      case AuraProfileCode.Enum.ADVISOR:
        return AdvisorIllustration;
      case AuraProfileCode.Enum.ANALYST:
        return AnalystIllustration;
      case AuraProfileCode.Enum.ANIMATOR:
        return AnimatorIllustration;
      case AuraProfileCode.Enum.COACH:
        return CoachIllustration;
      case AuraProfileCode.Enum.PEACEMAKER:
        return PeacemakerIllustration;
      case AuraProfileCode.Enum.PILOT:
        return PilotIllustration;
      case AuraProfileCode.Enum.PLANNER:
        return PlannerIllustration;
      case AuraProfileCode.Enum.PROTECTOR:
        return ProtectorIllustration;
      default:
        return DefaultIllustration;
    }
  }, [profileType]);

  const { i18n } = useLingui();
  const { locale = LanguageCode.Enum.fr } = i18n;
  const quarkUrl = buildQuarkUrl(locale as LanguageCodeT);

  return (
    <Stack
      component="article"
      bgcolor={firstSkill ? 'background.primary' : 'white'}
      borderRadius="3rem"
      p={2}
      zIndex={10}
      minWidth={{ none: '100%', sm: '45%', lg: '31%' }}
      sx={{
        boxShadow: '2px 2px 7px 0px rgba(0,0,0,0.3)',
        flex: '1 1 0%',
        order: order,
      }}
      direction="column"
      alignItems="center"
      spacing={2}
    >
      <Box component="div" position="relative" width="10.5rem" height="10.5rem">
        <Image
          src={firstSkill ? img : QuarkLogo}
          alt={t`Compétences Quark | PRISMO`}
          style={{ objectFit: 'contain', objectPosition: 'center' }}
          fill
        />
      </Box>
      {firstSkill && (
        <>
          <Stack direction="row" gap="3px">
            <Typography variant="h3">
              <Trans>Quark</Trans>
            </Typography>
            <Typography variant="h3">
              <Trans>|</Trans>
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 'normal' }}>
              <Trans>Compétence technique principale :</Trans>
            </Typography>
          </Stack>
          <Box
            component="div"
            width="fit-content"
            marginX="auto"
            bgcolor="background.primary"
            borderRadius={1_000_000}
          >
            <Typography
              color="primary.main"
              textAlign="center"
              paddingY={1}
              paddingX={2}
              variant="h4"
              fontWeight="bold"
            >
              {firstSkill}
            </Typography>
          </Box>
          <Box component="div">
            <ButtonLink
              app={Application.APP}
              path="/profil/:user/competences-quark"
              params={{ user: userUrl }}
              prefetch={false}
              variant="text"
              color="inherit"
              sx={{
                p: 0,
                '& .MuiButton-startIcon': {
                  display: 'none',
                },
              }}
            >
              <Trans>En savoir plus</Trans>
            </ButtonLink>
          </Box>
        </>
      )}
      {!firstSkill && isMe && (
        <>
          <Box component="div">
            <Button
              LinkComponent={Link}
              href={quarkUrl}
              variant="contained"
              color="gradient"
              sx={{
                '& .MuiButton-startIcon': {
                  display: 'none',
                },
              }}
            >
              <Trans>Passer Quark</Trans>
            </Button>
          </Box>
          <Typography variant="h3">
            <Trans>Identifiez vos compétences techniques !</Trans>
          </Typography>
        </>
      )}
      {!firstSkill && !isMe && (
        <Typography paddingTop={5} variant="h3">
          <Trans>Cet utilisateur n'a pas encore passé Quark !</Trans>
        </Typography>
      )}
    </Stack>
  );
};
